import type { Spx } from '@wix/thunderbolt-becky-types'
import type {
	AxisPosition,
	AxisRepeat,
	BackgroundLayer,
	ConicGradient,
	LinearGradient,
	RadialGradient,
} from '@wix/thunderbolt-becky-root'
// Registry imports
import { getConverter } from '../../utils/convertersRegistry'
import {
	getStringFromConverter,
	getKeywordString,
	getLengthString,
	getPercentageString,
} from '../../utils/converterHelpers'

/** Internal Converters */
const singleAxisPositionConverter = (axis: AxisPosition['x'] | AxisPosition['y']): string => {
	if (axis.type === 'OffsetPosition') {
		const position = getStringFromConverter(getKeywordString(axis.position))
		const offsetConverter = getConverter(axis.offset.type)
		const offset = offsetConverter && getStringFromConverter(offsetConverter(axis.offset))

		return [position, offset].filter(Boolean).join(' ')
	}

	const converter = getConverter(axis.type)
	return converter ? getStringFromConverter(converter(axis)) : ''
}

const axisPositionConverter = (prop: AxisPosition): Record<string, string> => {
	const { x: xProp, y: yProp } = prop
	const x = xProp && singleAxisPositionConverter(xProp)
	const y = yProp && singleAxisPositionConverter(yProp)

	return {
		x,
		y,
		[prop.type]: 'var(--{propName}-x) var(--{propName}-y)',
	}
}

const positionConverter = (
	prop: BackgroundLayer['position'] | RadialGradient['position'] | ConicGradient['position']
): string => {
	if (!prop) {
		return ''
	}

	if (prop.type === 'AxisPosition') {
		const axisPosition = axisPositionConverter(prop)

		return [axisPosition.x, axisPosition.y].filter(Boolean).join(' ')
	}

	const converter = getConverter(prop.type)
	return converter ? getStringFromConverter(converter(prop)) : ''
}

const axisRepeatConverter = (prop: AxisRepeat): string => {
	const { x, y } = prop

	return [x, y].filter(Boolean).join(' ')
}

const colorStopsConverter = (stops: LinearGradient['colorStops']): string => {
	return stops
		.map((stop) => {
			const colorConverter = getConverter(stop.color.type)
			const color = colorConverter && getStringFromConverter(colorConverter(stop.color))

			const sizeConverter = stop.size && getConverter(stop.size.type)
			const size = stop.size && sizeConverter && getStringFromConverter(sizeConverter(stop.size))

			const hintConverter = stop.hint && getConverter(stop.hint.type)
			const hint = stop.hint && hintConverter && getStringFromConverter(hintConverter(stop.hint))

			return [[color, size].filter(Boolean).join(' '), hint].filter(Boolean).join(', ')
		})
		.join(', ')
}

const angularColorStopsConverter = (stops: ConicGradient['colorStops']): string => {
	return stops
		.map((stop) => {
			const colorConverter = getConverter(stop.color.type)
			const color = colorConverter && getStringFromConverter(colorConverter(stop.color))

			const startConverter = stop.start && getConverter(stop.start.type)
			const start = stop.start && startConverter && getStringFromConverter(startConverter(stop.start))

			const endConverter = stop.end && getConverter(stop.end.type)
			const end = stop.end && endConverter && getStringFromConverter(endConverter(stop.end))

			const hintConverter = stop.hint && getConverter(stop.hint.type)
			const hint = stop.hint && hintConverter && getStringFromConverter(hintConverter(stop.hint))

			return [[color, start, end].filter(Boolean).join(' '), hint].filter(Boolean).join(', ')
		})
		.join(', ')
}

const directionConverter = (direction: LinearGradient['direction']): string => {
	if (!direction) {
		return ''
	}

	if (direction.type === 'Keyword') {
		const result = getStringFromConverter(getKeywordString(direction))
		return `to ${result}`
	}

	const converter = getConverter(direction.type)
	return converter ? getStringFromConverter(converter(direction)) : ''
}

const linearGradientConverter = (prop: LinearGradient): string => {
	const { direction: directionProp, colorStops: colorStopsProp, repeat } = prop
	const direction = directionConverter(directionProp)
	const colorStops = colorStopsProp && colorStopsConverter(colorStopsProp)

	const gradient = [direction, colorStops].filter(Boolean).join(', ')

	if (repeat) {
		return `repeating-linear-gradient(${gradient})`
	}

	return `linear-gradient(${gradient})`
}

const radialGradientConverter = (prop: RadialGradient): string => {
	const { position: positionProp, shape: shapeProp, size: sizeProp, colorStops: colorStopsProp, repeat } = prop

	const shape = shapeProp && getStringFromConverter(getKeywordString(shapeProp))
	const position = positionProp && `at ${positionConverter(positionProp)}`
	const size = sizeProp && sizeConverter()[sizeProp.type]?.(sizeProp)

	const colorStops = colorStopsConverter(colorStopsProp)

	const shapeAtPositionOrSize = [shape, position || size].filter(Boolean).join(' ')

	const gradient = [shapeAtPositionOrSize, colorStops].filter(Boolean).join(', ')

	if (repeat) {
		return `repeating-radial-gradient(${gradient})`
	}

	return `radial-gradient(${gradient})`
}

const colorInterpolationMethodConverter = (prop: ConicGradient['colorInterpolationMethod']): string => {
	if (!prop) {
		return ''
	}

	const method = `in ${prop.method}`
	const hue = prop.hueInterpolationMethod && `${prop.hueInterpolationMethod} hue`

	return [method, hue].filter(Boolean).join(' ')
}

const conicGradientConverter = (prop: ConicGradient): string => {
	const {
		colorInterpolationMethod: colorInterpolationMethodProp,
		colorStops: colorStopsProp,
		direction: directionProp,
		position: positionProp,
		repeat,
	} = prop

	const angleConverter = getConverter('Angle')
	const fromAngle = directionProp && angleConverter && `from ${getStringFromConverter(angleConverter(directionProp))}`

	const position = positionProp && `at ${positionConverter(positionProp)}`
	const colorInterpolationMethod =
		colorInterpolationMethodProp && colorInterpolationMethodConverter(colorInterpolationMethodProp)

	const fromAngleWithPositionOrColorInterpolationMethod = [fromAngle, position || colorInterpolationMethod]
		.filter(Boolean)
		.join(' ')

	const colorStops = colorStopsProp && angularColorStopsConverter(colorStopsProp)

	const gradient = [fromAngleWithPositionOrColorInterpolationMethod, colorStops].filter(Boolean).join(', ')

	if (repeat) {
		return `repeating-conic-gradient(${gradient})`
	}

	return `conic-gradient(${gradient})`
}

const backgroundLayerConverter = (prop: BackgroundLayer, spx?: Spx) => {
	const image = prop.image && backgroundImageConverterMap()[prop.image.type]?.(prop.image)
	const position = prop.position && positionConverter(prop.position)
	// @ts-ignore waiting on PR from DM to fix this
	const size = prop.size && sizeConverter()[prop.size.type]?.(prop.size, spx)

	// Handle background repeat properly based on type
	let repeat: string | undefined
	if (prop.repeat) {
		if (prop.repeat.type === 'AxisRepeat') {
			repeat = axisRepeatConverter(prop.repeat)
		} else {
			repeat = getStringFromConverter(getKeywordString(prop.repeat))
		}
	}

	const attachment = prop.attachment && getStringFromConverter(getKeywordString(prop.attachment))
	const clip = prop.clip && getStringFromConverter(getKeywordString(prop.clip))
	const origin = prop.origin && getStringFromConverter(getKeywordString(prop.origin))

	const colorConverter = prop.color && getConverter(prop.color.type)
	const color = prop.color && colorConverter && getStringFromConverter(colorConverter(prop.color))

	return {
		color,
		image,
		position,
		repeat,
		size,
		attachment,
		clip,
		origin,
		[prop.type]:
			'var(--{propName}-image) var(--{propName}-position) / var(--{propName}-size) var(--{propName}-repeat) var(--{propName}-attachment) var(--{propName}-origin) var(--{propName}-clip) var(--{propName}-color)',
	}
}

/** Internal Maps (using registry-based lookups) */
const backgroundImageConverterMap = () => ({
	Url: getConverter('Url'),
	Keyword: getKeywordString,
	LinearGradient: linearGradientConverter,
	RadialGradient: radialGradientConverter,
	ConicGradient: conicGradientConverter,
})

const sizeConverter = () => ({
	Keyword: getKeywordString,
	Length: getLengthString,
	Percentage: getPercentageString,
	Dimensions: getConverter('Dimensions'),
})

/** External Map (kept for backward compatibility) */
export const backgroundConverterMap = {
	BackgroundLayer: backgroundLayerConverter,
	AxisPosition: axisPositionConverter,
	AxisRepeat: axisRepeatConverter,
	LinearGradient: linearGradientConverter,
	RadialGradient: radialGradientConverter,
	ConicGradient: conicGradientConverter,
}
